import {WriteButton, WriteFileInput, WriteIconButton} from '../../common/buttons/WriteActions';
import {rememberOriginalName} from '../../../services/SignedImageUpload';
import {getCurrencySymbol} from '../../../common/currency';
import {useEffect, useRef, useState} from 'react';
import {Autocomplete, Box, Card, CardMedia, Fade, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, Stack, TextField, Tooltip} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {DTO_STATE, RECIPE_SIZE} from '@mybake/ui/common/dictionary';
import {IRecipe} from '../../../models/Recipe/IRecipe';
import {ISubOrder} from '../../../models/Order/ISubOrder';
import CloseIcon from '@mui/icons-material/Close';
import {IOrderImage} from '../../../models/Order/IOrderImage';
import {PackageSelector} from "../PackageSelector";
import {IPackage} from "../../../models/Packages/IPackage";
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {IDecoration} from "../../../models/Decorations/IDecoration";
import {DecorationSelector} from "../DecorationSelector";
import Collapse from "@mui/material/Collapse";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {calculateSubOrderCost} from '../subOrderCost';
import {useCurrency} from '../../../hooks/useCurrency';
import {subOrderSurfaceSx} from '../subOrderStyles';
import {userAPI} from '../../../services/UserServices';
import {IProductType} from '../../../models/ProductTypes/IProductType';
import {calculateRecommendedPrice} from '../recommendedPrice';
import {LocalizedDecimalField} from '../../common/inputs/LocalizedDecimalField';

interface ICreateSubOrderProps {
    subOrder: ISubOrder;
    recipes: IRecipe[] | undefined;
    packages: IPackage[] | undefined;
    decorations: IDecoration[] | undefined;
    productTypes: IProductType[] | undefined;
    handleUpdateSubOrders: (subOrder: ISubOrder) => void;
    deleteSubOrder: (id: string | number) => void;
    setFile: (files: File[]) => void;
    removeFile: (fileName: string) => void;
}

const CreateSubOrder = ({
                            subOrder,
                            recipes,
                            packages,
                            decorations,
                            productTypes,
                            handleUpdateSubOrders,
                            deleteSubOrder,
                            setFile,
                            removeFile
                        }: ICreateSubOrderProps) => {
    const {currency, formatCurrency} = useCurrency();
    const {data: user} = userAPI.useGetUserDataQuery();
    const [newSubOrder, setNewSubOrder] = useState<ISubOrder>({...subOrder});
    const {t} = useAppTranslation();
    const [newPrice, setNewPrice] = useState<number>(subOrder.price);
    const [orderCount, setOrderCount] = useState<number>(1);
    const [productCost, setProductCost] = useState<number>(1);
    const [recipeCost, setRecipeCost] = useState(0);
    const [isValueSelected, setIsValueSelected] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState<IRecipe>();
    const inputCountRef = useRef<HTMLInputElement>(null);
    const inputPriceRef = useRef<HTMLInputElement>(null);
    const [fillingName, setFillingName] = useState('');
    const [lastFetchedRecipeId, setLastFetchedRecipeId] = useState<string | null>(null);
    const [profit, setProfit] = useState('');
    const [previewUrls, setPreviewUrls] = useState<string[] | null>(null);
    const [fileMap, setFileMap] = useState<Map<string, File>>(new Map());
    const [urlMap, setUrlMap] = useState<Map<string, string>>(new Map());
    const [fileHashes, setFileHashes] = useState<Set<string>>(new Set());
    const buttonStyle = {lineHeight: '1.25rem' /* fontSize: '0.75rem' */};
    const autoCompleteLabelStyle = {'& .MuiInputLabel-root': {pointerEvents: 'none'}};
    let recipesNameList: string[] = [];
    recipes?.map((r) => recipesNameList.push(r.recipeName || ''));

    const decorationOptions =
        decorations?.map(d => ({
            id: d.id,
            name: d.name ?? '',
        })) ?? [];

    const selectedIds =
        (subOrder?.decorations ?? []).map(d => d.decorationId);

    const availableDecorations = decorationOptions.filter(
        d => !selectedIds.includes(d.id)
    );
    const recommendation = calculateRecommendedPrice({recipe: selectedRecipe, selectedProportion: newSubOrder.recipeCounter, count: newSubOrder.count, selectedDecorations: newSubOrder.decorations, productTypes, decorations});

    const handleRecipeName = (e: any, name: string | null) => {
        setIsValueSelected(!!name);
        if (name) {
            recipes?.forEach((r) => {
                const recipe = r.recipeName === name;

                if (recipe) {
                    setNewSubOrder((prevState) => {
                        return {
                            ...prevState,
                            recipeName: name,
                            recipeId: r.id,
                        };
                    });

                    setSelectedRecipe(r);
                    //calculateProductCost();
                    //setProfit(Number(newSubOrder.price - newSubOrder.productCost).toFixed(2));
                    /*if (r.isLinear) {
                        setRecipeSize(getRecipeSize(r));
                    } else {
                        const sizes = r.proportionInfos.map((pi) => pi.proportion);
                        setRecipeSize(sizes);
                    }*/

                    return;
                }
            });
        }
    };

    const handleRecipeSize = (e: any, counter: any) => {
        if (counter) {
            setNewSubOrder((prevState) => ({
                ...prevState,
                recipeCounter: counter.props.value,
            }));
        }
    };

    const handleFilling = (e: any, name: any) => {
        if (name) {
            setFillingName(name.props.value);
            const fillingId =
                selectedRecipe?.fillings && selectedRecipe?.fillings.length > 0 ? selectedRecipe.fillings.find((el) => el.fillingName === name.props.value)?.id || 0 : undefined;
            if (fillingId !== undefined) {
                setNewSubOrder((prev) => {
                    return {
                        ...prev,
                        fillingId: fillingId,
                    };
                });
            }

        }
    };

    const handleAddPackageField = () => {
        setNewSubOrder((prev) => ({
            ...prev,
            packages: [
                ...(prev.packages || []),
                {
                    id: crypto.randomUUID(),
                    packageId: 0 - (newSubOrder.packages?.length ?? 0),
                    name: '', // Пустое значение по умолчанию
                    quantity: 1,
                    state: DTO_STATE.DTO_NEW
                }
            ]
        }));
    };

    const handleAddDecorationField = () => {
        setNewSubOrder((prev) => ({
            ...prev,
            decorations: [
                ...(prev.decorations || []),
                {
                    id: crypto.randomUUID(),
                    decorationId: '',
                    name: '',
                    quantity: 0,
                    state: DTO_STATE.DTO_NEW
                }
            ]
        }));
    };

    const handleUpdateDecorationName = (id: string, decorationName: string) => {
        const decorationId = decorations?.find(x => x.name === decorationName)?.id || '';

        setNewSubOrder((prev) => ({
            ...prev,
            decorations: (prev.decorations ?? []).map((dec) =>
                dec.id === id
                    ? {...dec, decorationId, name: decorationName}
                    : dec
            ),
        }));
    }

    const handleUpdateDecorationCount = (id: string, quantity: number) => {
        setNewSubOrder((prev) => ({
            ...prev,
            decorations: (prev.decorations ?? []).map((dec) =>
                dec.id === id
                    ? {...dec, quantity: Math.max(0, quantity)}
                    : dec
            ),
        }));
    }

    const handleRemoveDecoration = (id: string) => {
        setNewSubOrder((prev) => ({
            ...prev,
            decorations: (prev.decorations ?? []).filter(
                dec => dec.id !== id
            ),
        }));
    }

    const handleUpdatePackageField = (index: number, updatedValue: {
        packageId: number | string;
        name: string;
        quantity: number
    }) => {
        setNewSubOrder((prev) => {
            const newPackages = [...(prev.packages || [])];
            newPackages[index] = {
                ...newPackages[index],
                packageId: updatedValue.packageId,
                name: updatedValue.name,
                quantity: updatedValue.quantity
            };
            return {...prev, packages: newPackages};
        });
    };

    const handleRemovePackageField = (index: number) => {
        setNewSubOrder((prev) => ({
            ...prev,
            packages: (prev.packages || []).filter((_, i) => i !== index)
        }));
    };
    console.log(subOrder.decorations)
    const selectedPackagesNames: string[] = newSubOrder.packages?.map(p => p.name) || [];
    const selectedPackagesCount = subOrder.packages?.length || 0;
    const availableOptionsCount = packages?.length || 0;
    const isAddDisabled = selectedPackagesCount >= availableOptionsCount;

    const handleOrderCount = (e: any) => {
        const qty = Number(e.target.value);

        if (qty >= 0) {
            const cost = recipeCost * newSubOrder.recipeCounter * qty;
            setOrderCount(qty);
            setNewSubOrder((prevState) => ({
                ...prevState,
                count: qty,
            }));
        }
    };

    const handlePrice = (value: number | undefined) => {
        const price = value ?? 0;
        if (price >= 0) {
            setNewPrice(price);
            setNewSubOrder((prevState) => ({
                ...prevState,
                price,
            }));
        }
    };

    const size =
        newSubOrder.recipeCounter.toString().length === 1 && newSubOrder.recipeCounter !== 0
            ? `${newSubOrder.recipeCounter}.00`
            : newSubOrder.recipeCounter.toString().length === 3
                ? `${newSubOrder.recipeCounter}0`
                : newSubOrder.recipeCounter === 0
                    ? ''
                    : newSubOrder.recipeCounter.toString() || '1.00';


    const recipeCounterError = newSubOrder.recipeCounter === 0;
    const fillingError = newSubOrder.fillingId === 0;
    const calculateProductCost = () => {
        const tempCost = calculateSubOrderCost({
            recipe: selectedRecipe,
            fillingId: newSubOrder.fillingId,
            count: newSubOrder.count,
            recipeCounter: newSubOrder.recipeCounter,
            additionalRecipeCost: user?.additionalRecipeCost,
            packages,
            selectedPackages: newSubOrder.packages,
            decorations,
            selectedDecorations: newSubOrder.decorations,
        });
        setProductCost(tempCost);
        setNewSubOrder((prev) => {
            return {
                ...prev,
                productCost: tempCost,
            };
        });
        setRecipeCost(tempCost);
        setProfit(Number(newSubOrder.price - tempCost).toFixed(2));
    };

    const handleRemoveImage = async (previewUrl: string) => {
        const originalFileName = urlMap.get(previewUrl);

        if (!originalFileName) return;

        const fileToRemove = fileMap.get(originalFileName);
        if (!fileToRemove) return;

        const hash = await getFileHash(fileToRemove);

        setPreviewUrls((prevUrls) => (prevUrls ? prevUrls.filter((url) => url !== previewUrl) : null));

        setFileMap((prevMap) => {
            const newMap = new Map(prevMap);
            newMap.delete(originalFileName);
            return newMap;
        });

        setUrlMap((prevMap) => {
            const newMap = new Map(prevMap);
            newMap.delete(previewUrl);
            return newMap;
        });

        setFileHashes((prevHashes) => {
            const newSet = new Set(prevHashes);
            newSet.delete(hash);
            return newSet;
        });

        removeFile(fileToRemove.name); // если нужно удаление вне компонента
    };

    const getFileHash = async (file: File): Promise<string> => {
        const buffer = await file.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
        return Array.from(new Uint8Array(hashBuffer))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
    };

    const uploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        const files = Array.from(e.target.files);
        const newFileMap = new Map(fileMap);
        const newUrlMap = new Map(urlMap);
        const newHashes = new Set(fileHashes);
        const updatedPreviewUrls: string[] = [];

        for (const file of files) {
            const hash = await getFileHash(file);

            if (newHashes.has(hash)) {
                console.log(`Файл с таким содержимым уже загружен: ${file.name}`);
                continue;
            }

            const renamedFile = rememberOriginalName(new File([file], crypto.randomUUID(), {
                type: file.type,
                lastModified: file.lastModified,
            }), file);

            const previewUrl = URL.createObjectURL(renamedFile);

            newFileMap.set(renamedFile.name, renamedFile); // используем новое имя
            newUrlMap.set(previewUrl, renamedFile.name);
            newHashes.add(hash);
            updatedPreviewUrls.push(previewUrl);
        }

        if (updatedPreviewUrls.length > 0) {
            setPreviewUrls((prev) => [...(prev || []), ...updatedPreviewUrls]);
            setFileMap(newFileMap);
            setUrlMap(newUrlMap);
            setFileHashes(newHashes);
        }

        e.target.value = ''; // очистка input
    };

    useEffect(() => {
        const files = Array.from(fileMap.values());
        const images: IOrderImage[] = files.map((el) => {
            return {
                id: el.name,
                subOrderId: newSubOrder.id,
                orderId: newSubOrder.orderId,
                fileName: el.name,
                isResultImage: false,
                state: DTO_STATE.DTO_NEW
            };
        });
        const subOrderImages = newSubOrder.images?.length || 0;

        setNewSubOrder((prev) => {
            return {
                ...prev,
                images: images,
            };
        });

        if (images.length > subOrderImages) {
            setFile(files);
        }
    }, [fileMap]);

    useEffect(() => {
        if (newSubOrder.fillingId !== undefined && newSubOrder.fillingId !== 0 && (!selectedRecipe?.fillings || selectedRecipe.fillings.length == 0)) {
            setFillingName('');
            setNewSubOrder((prev) => {
                return {
                    ...prev,
                    fillingName: '',
                };
            });
        }

        handleUpdateSubOrders(newSubOrder);
    }, [newSubOrder]);

    useEffect(() => {
        calculateProductCost();
    }, [selectedRecipe, newSubOrder.fillingId, newSubOrder.count, newSubOrder.recipeCounter, newSubOrder.price, newSubOrder.packages, newSubOrder.decorations, packages, decorations, user?.additionalRecipeCost]);

    /*    useEffect(() => {
                if (!selectedRecipe?.id) return;
                if (selectedRecipe?.productTypes !== undefined && selectedRecipe?.productTypes.length > 0) {
                    searchPackages(selectedRecipe.productTypes.map(x => x.id));
                }
            }
            , [selectedRecipe?.id])*/

    useEffect(() => {
        if (!selectedRecipe?.id) {
            setLastFetchedRecipeId(null);
            setNewSubOrder(prev => ({...prev, packages: []}));
            return;
        }
        setNewSubOrder(prev => ({...prev, packages: []}));
        setLastFetchedRecipeId(selectedRecipe.id as string);
    }, [selectedRecipe?.id]);

    return (
        <Box sx={subOrderSurfaceSx}>
            <Stack spacing={2} sx={{minWidth: 100, maxWidth: 390}}>
                <Autocomplete
                    disablePortal
                    id="recipes"
                    options={recipesNameList}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('recipes:select_recipe')}
                            placeholder={t('orders:recipe_not_selected')}
                            error={!newSubOrder.recipeName}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                ...params.InputProps,

                                endAdornment: (
                                    <>
                                        {!newSubOrder.recipeName && (
                                            <InputAdornment position="end">
                                                <Tooltip
                                                    title={t('recipes:select_recipe')}
                                                    arrow
                                                >
                                                    <ErrorOutlineRoundedIcon
                                                        color="error"
                                                        fontSize="small"
                                                        style={{
                                                            cursor: 'pointer',
                                                            opacity: 0.7,
                                                            marginRight: 4,
                                                        }}
                                                    />
                                                </Tooltip>
                                            </InputAdornment>
                                        )}

                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    noOptionsText={t('recipes:recipes_not_found')}
                    clearOnEscape
                    onChange={handleRecipeName}
                    sx={{
                        ...autoCompleteLabelStyle,
                        '&.MuiAutocomplete-root .MuiInputLabel-root': {
                            color: isValueSelected ? 'green' : 'warning.main',
                        },
                    }}
                />
                <FormControl
                    variant="outlined"
                    size="medium"
                    error={recipeCounterError}
                    sx={{
                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '2px',
                            borderColor: recipeCounterError ? 'red' : 'green',
                        },
                    }}
                >
                    <InputLabel id="recipe_size_label">{t('orders:recipe_size_kg')}</InputLabel>
                    <Select
                        labelId="recipe_size_label"
                        id="create_recipe_select"
                        value={size}
                        onChange={handleRecipeSize}
                        label={t('orders:recipe_size_kg')}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderWidth: '2px',
                                    borderColor: isValueSelected ? 'green' : 'rgb(211, 47, 47)',
                                },
                                '&:hover fieldset': {
                                    borderWidth: '2px',
                                    borderColor: isValueSelected ? 'green' : 'rgb(211, 47, 47)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderWidth: '2px',
                                    borderColor: isValueSelected ? 'green' : 'rgb(211, 47, 47)',
                                },
                            },
                        }}
                    >
                        {RECIPE_SIZE.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {selectedRecipe?.fillings && selectedRecipe.fillings.length > 0 && (
                    <FormControl
                        variant="outlined"
                        size="medium"
                        //error={fillingError}
                        sx={{
                            '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                borderWidth: '2px',
                                borderColor: fillingError ? 'warning.main' : 'green',
                            },
                        }}
                    >
                        <InputLabel id="filling_label">{t('recipes:filling')}</InputLabel>
                        <Select
                            labelId="filling_label"
                            id="filling_select"
                            value={fillingName}
                            onChange={handleFilling}
                            label={t('recipes:filling')}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderWidth: '2px',
                                        borderColor: isValueSelected ? 'green' : 'warning.main',
                                    },
                                    '&:hover fieldset': {
                                        borderWidth: '2px',
                                        borderColor: isValueSelected ? 'green' : 'warning.main',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderWidth: '2px',
                                        borderColor: isValueSelected ? 'green' : 'warning.main',
                                    },
                                },
                            }}
                        >
                            {selectedRecipe.fillings.map((option) => (
                                <MenuItem key={option.fillingName} value={option.fillingName}>
                                    {option.fillingName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {packages && lastFetchedRecipeId === selectedRecipe?.id && (
                    <Box sx={{mt: 2}}>
                        {subOrder.packages?.map((pkg, index) => (
                            <PackageSelector
                                key={pkg.id}
                                options={packages}
                                value={{packageId: pkg.packageId, name: pkg.name, quantity: pkg.quantity}}
                                label={t('settings:packages')}
                                excludedNames={selectedPackagesNames}
                                onChange={(updatedValue) => handleUpdatePackageField(index, updatedValue)}
                                onRemove={() => handleRemovePackageField(index)}
                            />
                        ))}

                        <WriteButton
                            variant="outlined"
                            startIcon={<AddIcon/>} // Импортируйте AddIcon из @mui/icons-material
                            onClick={handleAddPackageField}
                            disabled={isAddDisabled} // Блокируем кнопку здесь
                            sx={{
                                mt: 1,
                                width: '100%',
                                // Опционально: можно добавить визуальный стиль для disabled состояния
                                '&.Mui-disabled': {
                                    opacity: 0.5
                                }
                            }}
                        >
                            {t('orders:add_package')}
                        </WriteButton>
                    </Box>)}

                <Collapse
                    in={!!decorations?.length}
                    timeout={500}
                    unmountOnExit
                >
                    <Box>
                        <Stack spacing={2} sx={{minWidth: 100, maxWidth: 390}}>
                            {subOrder.decorations?.map((dec, index) => (
                                <Fade in={true} timeout={750}>
                                    <div>
                                        <DecorationSelector
                                            key={dec.id}
                                            options={availableDecorations.map(d => d.name)}
                                            id={dec.id}
                                            name={dec.name}
                                            quantity={dec.quantity}
                                            onChangeName={handleUpdateDecorationName}
                                            onChangeCount={handleUpdateDecorationCount}
                                            onRemove={handleRemoveDecoration}
                                            //value={{packageId: pkg.packageId, name: pkg.name, quantity: pkg.quantity}}
                                            //label={getTitle(language, FIELDS.PACKAGES.PACKAGES)}
                                            //excludedNames={selectedPackagesNames}
                                            //onChange={(updatedValue) => handleUpdatePackageField(index, updatedValue)}
                                            //onRemove={() => handleRemovePackageField(index)}
                                        />
                                    </div>
                                </Fade>
                            ))}
                        </Stack>
                        <WriteButton
                            variant="outlined"
                            startIcon={<AddIcon/>}
                            onClick={handleAddDecorationField}
                            disabled={decorations == undefined || selectedIds.length === decorations.length}
                            sx={{
                                mt: 1,
                                width: '100%',
                                // Опционально: можно добавить визуальный стиль для disabled состояния
                                '&.Mui-disabled': {
                                    opacity: 0.5
                                }
                            }}
                        >
                            {t('orders:add_decoration')}
                        </WriteButton>
                    </Box>
                </Collapse>

                <TextField
                    inputRef={inputCountRef}
                    id={'count'}
                    label={t('items:count')}
                    placeholder={t('items:count')}
                    value={orderCount}
                    focused
                    type="number"
                    onChange={handleOrderCount}
                    onFocus={() => inputCountRef.current && inputCountRef.current.select()}
                    color={orderCount === 0 ? 'error' : 'success'}
                    style={{marginBottom: 10}}
                    sx={autoCompleteLabelStyle}
                />
                <LocalizedDecimalField
                    inputRef={inputPriceRef}
                    id={'price'}
                    label={t('finance:order_price')}
                    placeholder={t('finance:order_price')}
                    value={newPrice}
                    focused
                    onValueChange={handlePrice}
                    onFocus={() => inputPriceRef.current && inputPriceRef.current.select()}
                    color={newSubOrder.price === 0 ? 'warning' : 'success'}
                    style={{marginBottom: 10}}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">{getCurrencySymbol(currency)}</InputAdornment>,
                    }}
                    sx={autoCompleteLabelStyle}
                />

                {Number(newPrice) < Number(newSubOrder.productCost) && <Box sx={{p: 1.5, borderRadius: 1, bgcolor: 'warning.light', color: 'warning.dark'}}>
                    {t('orders:price_below_cost', {amount: formatCurrency(Number(newSubOrder.productCost) - Number(newPrice))})}
                </Box>}

                {selectedRecipe && <Box sx={{p: 1.5, borderRadius: 1, bgcolor: 'action.hover'}}>
                    <Box>{t('orders:recommended_price')}: {formatCurrency(recommendation.linePrice ?? 0)}</Box>
                    {Number(newPrice) !== recommendation.linePrice && <WriteButton size="small" onClick={() => {
                        const price = recommendation.linePrice ?? 0;
                        setNewPrice(price);
                        setNewSubOrder(previous => ({...previous, price}));
                    }}>{t('orders:use_recommended_price')}</WriteButton>}
                    {!recommendation.isComplete && <Box sx={{color: 'warning.main', mt: 0.5}}>{t('orders:recommended_price_missing_for')}: {recommendation.missing.join(', ')}</Box>}
                </Box>}

                <WriteFileInput style={{display: 'none'}} id={`file-upload_${subOrder.id}`} type="file" multiple
                       accept=".jpg,.png,.jpeg" onChange={uploadFile}/>

                <label htmlFor={`file-upload_${subOrder.id}`}>
                    <WriteButton variant="contained" component="span">
                        {t('orders:upload_order_images')}
                    </WriteButton>
                </label>

                {previewUrls && (
                    <Grid container spacing={2} sx={{mt: 2}}>
                        {previewUrls.map((url, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card sx={{maxWidth: 345, position: 'relative'}}>
                                    <WriteIconButton
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            zIndex: 1,
                                            backgroundColor: 'rgba(255, 255, 255, 0.7)'
                                        }}
                                        onClick={() => handleRemoveImage(url || '')}
                                    >
                                        <CloseIcon/>
                                    </WriteIconButton>
                                    <CardMedia component="img" height="200" image={url} alt={`Preview ${index + 1}`}/>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Box>
                    {t('finance:order_price')} {formatCurrency(Number(newPrice))}
                </Box>
                <Box>
                    {t('finance:order_cost')} {formatCurrency(Number(newSubOrder.productCost))}
                </Box>
                <Box>
                    {t('finance:order_profit')} {formatCurrency(profit)}
                </Box>
                <WriteButton variant="outlined" onClick={() => deleteSubOrder(subOrder.id)}
                        sx={buttonStyle}>
                    {t('orders:delete_suborder')}
                </WriteButton>
            </Stack>
        </Box>
    );
};

export default CreateSubOrder;
